import type { ScheduledTask } from 'src/pages/operations/types/scheduler.types';
import { formatScheduledTimeForDisplay } from './scheduler-time.helpers';

/** Matches backend scheduler.validators.CRON_FIELD_PATTERN */
const CRON_FIELD_PATTERN = /^[\d*,\-/]+$/;

/** Returns an error message, or null when the expression looks valid. */
export function validateCronExpression(value: string | null | undefined): string | null {
  const expression = (value ?? '').trim();
  if (!expression) {
    return 'Cron expression is required.';
  }

  const parts = expression.split(/\s+/).filter(Boolean);
  if (parts.length !== 5 && parts.length !== 6) {
    return 'Use 5 or 6 fields (e.g. "0 9 * * *").';
  }

  const invalidPart = parts.find((part) => !CRON_FIELD_PATTERN.test(part));
  if (invalidPart) {
    return `Invalid cron field: ${invalidPart}`;
  }

  return null;
}

export function formatScheduleForDisplay(task: Pick<
ScheduledTask,
'schedule_type' | 'scheduled_time' | 'cron_expression'
>): string {
  if (task.schedule_type === 'cron') {
    return task.cron_expression?.trim() || '—';
  }
  return formatScheduledTimeForDisplay(task.scheduled_time);
}

export function scheduleSortKey(task: Pick<
ScheduledTask,
'schedule_type' | 'scheduled_time' | 'cron_expression' | 'name'
>): string {
  if (task.schedule_type === 'cron') {
    return `cron:${task.cron_expression ?? ''}:${task.name}`;
  }
  return `time:${task.scheduled_time ?? ''}:${task.name}`;
}

export function formatSchedulerMutationError(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return 'Could not save the scheduled task.';
  }

  const data = 'data' in error ? (error as { data?: unknown }).data : undefined;
  if (!data || typeof data !== 'object') {
    return 'Could not save the scheduled task.';
  }

  const record = data as Record<string, unknown>;
  if (typeof record.message === 'string') {
    return record.message;
  }

  const fieldMessages = Object.entries(record)
    .flatMap(([field, value]) => {
      if (Array.isArray(value)) {
        return value.map((message) => `${field}: ${String(message)}`);
      }
      if (typeof value === 'string') {
        return [`${field}: ${value}`];
      }
      return [];
    });

  if (fieldMessages.length) {
    return fieldMessages.join(' ');
  }

  return 'Could not save the scheduled task.';
}
